/**
 * React hook for notification integration
 * Provides easy integration of notifications with app features
 */

import { useEffect } from 'react';
import {
  sendStreakReminder,
  sendPartnerCheckInNotification,
  sendHighTensionAlert,
  scheduleDailyCheckInReminder,
} from '../lib/notification-service';

/**
 * Hook to track check-in completion and trigger notifications
 */
export function useCheckInNotifications() {
  useEffect(() => {
    // Listen for check-in completions
    const handleCheckInComplete = (event: CustomEvent) => {
      const { streak, rtiScore } = event.detail;
      
      // Send streak reminders at milestones
      if (streak && [3, 7, 14, 30, 60, 90, 100].includes(streak)) {
        sendStreakReminder(streak);
      }
      
      // Send high tension alerts
      if (rtiScore >= 61) {
        sendHighTensionAlert(rtiScore);
      }
      
      // Reschedule next day's reminder
      setTimeout(() => {
        scheduleDailyCheckInReminder();
      }, 1000);
    };
    
    window.addEventListener('checkin-complete' as any, handleCheckInComplete as EventListener);
    
    return () => {
      window.removeEventListener('checkin-complete' as any, handleCheckInComplete as EventListener);
    };
  }, []);
}

/**
 * Trigger check-in complete event (call from check-in page)
 */
export function triggerCheckInComplete(streak: number, rtiScore: number) {
  const event = new CustomEvent('checkin-complete', {
    detail: { streak, rtiScore }
  });
  window.dispatchEvent(event);
}

/**
 * Hook to track partner check-ins
 */
export function usePartnerNotifications() {
  useEffect(() => {
    // In a real multi-user system, this would listen to partner's check-ins
    // For now, this is a placeholder for when multiplayer features are added
    
    const handlePartnerCheckIn = (event: CustomEvent) => {
      const { partnerName } = event.detail;
      sendPartnerCheckInNotification(partnerName);
    };
    
    window.addEventListener('partner-checkin' as any, handlePartnerCheckIn as EventListener);
    
    return () => {
      window.removeEventListener('partner-checkin' as any, handlePartnerCheckIn as EventListener);
    };
  }, []);
}

/**
 * Trigger partner check-in event
 */
export function triggerPartnerCheckIn(partnerName: string) {
  const event = new CustomEvent('partner-checkin', {
    detail: { partnerName }
  });
  window.dispatchEvent(event);
}
